import React, { useContext, useMemo, useRef, useState, useLayoutEffect, useEffect } from "react"
import { View, Text, ScrollView } from "react-native"
import { useNavigation, useRoute } from "@react-navigation/native"
import { useAppSelector } from "../store"
import { ThemeContext } from "../utility/context/ThemeContext"
import { textStyles } from "../style"
import { currencyFormat } from "@ecountabl/lib"
import { MAX_CONTENT_WIDTH } from "../config"
import { AccountCard } from "../account-card"
import { CustomActionSheet, CustomActionSheetRef } from "../utility/CustomActionSheet"
import AccountManagement from "../utility/forms/account-management"
import { InstitutionDetailsSkeleton } from "./institution-details-skeleton"

const InstitutionDetails: React.FC = () => {
  const route = useRoute<any>()
  const item_id = route?.params?.item_id as string
  const navigation = useNavigation()

  const { accounts, institutions, loading } = useAppSelector((state) => state.accountsSlice)
  const theme = useContext(ThemeContext).theme
  const { textMain, bgMain } = theme.colors
  const manageAccountsSheetRef = useRef<CustomActionSheetRef>(null)
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)

  const institution = useMemo(
    () => institutions.find(i => i.item_id === item_id),
    [institutions, item_id]
  )
  const institutionAccounts = useMemo(
    () => accounts.filter(a => a.item_id === item_id),
    [accounts, item_id]
  )
  const totalBalance = useMemo(
    () => institution?.totalBalance || 0,
    [institution?.totalBalance]
  )

  // Set the header title to institution name
  useLayoutEffect(() => {
    const title = institution?.name || 'Institution'
    navigation.setOptions({ title })
  }, [institution?.name, navigation])

  const selectedAccount = useMemo(
    () => institutionAccounts.find(a => a.account_id === selectedAccountId) || null,
    [selectedAccountId, institutionAccounts]
  )

  useEffect(() => {
    if (!item_id) {
      console.error('InstitutionDetails: missing item_id')
      navigation.goBack()
    }
  }, [item_id, navigation])

  if (!item_id) {
    return null
  }

  // If we haven't hydrated institutions/accounts yet, show skeleton that matches the layout.
  if (loading || !institution) {
    return <InstitutionDetailsSkeleton accountCardCount={2} />
  }

  return (
    <>
      <View style={{ flex: 1, backgroundColor: bgMain }}>
        {/* Fixed stats section (always visible) */}
        <View
          style={{
            padding: 16,
            width: "100%",
            maxWidth: MAX_CONTENT_WIDTH,
            alignSelf: "center"
          }}
        >
          <View style={{ width: "100%", gap: 6 }}>
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Text style={[textStyles.label, { color: textMain }]}>Balance</Text>
              <Text style={[textStyles.label, { color: textMain }]}>Accounts</Text>
            </View>
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Text style={[textStyles.h3, { color: textMain }]}>
                {currencyFormat(totalBalance)}
                </Text>
              <Text style={[textStyles.h3, { color: textMain }]}>
                {institutionAccounts.length}
              </Text>
            </View>
          </View>
        </View>

        {/* Scrollable accounts list */}
        <ScrollView
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingBottom: 16,
            gap: 12,
            width: "100%",
            maxWidth: MAX_CONTENT_WIDTH,
            alignSelf: "center"
          }}
        >
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-evenly",
              alignItems: "center",
              gap: 12
            }}
          >
            {institutionAccounts.map(account => (
              <AccountCard
                key={account.account_id}
                account={account}
                onMenuPress={() => {
                  setSelectedAccountId(account.account_id)
                  manageAccountsSheetRef.current?.show()
                }}
              />
            ))}
          </View>
        </ScrollView>
      </View>

      <CustomActionSheet ref={manageAccountsSheetRef} name="manage-accounts">
        {selectedAccount && (
          <AccountManagement
            account={selectedAccount}
            closeSheet={() => {
              manageAccountsSheetRef.current?.hide()
            }}
          />
        )}
      </CustomActionSheet>
    </>
  )
}

export default InstitutionDetails

